import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import clsx from 'clsx';
import { ArrowDownLeft, ArrowUpRight, Camera, Check, CloudOff, Hourglass, Image, ImageOff, Maximize2, Plus, Receipt, RefreshCw, Trash, UserCog, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, PagedResponse, PettyCashEntry, PettyCashStatus, PettyCashSummary } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants';
import { formatCurrency, formatFileSize } from '@/utils/formatters';
import { classifyPettyCashError } from '@/utils/pettyCash';
import { Alert, Badge, Button, Chip, IconButton, Input, LoadingSkeleton, NumericKeypad, Overline, Sheet } from '@/components/ui';
import { PageShell } from '@/components/layout/PageShell';
import { useIsLandscape } from '@/hooks/useMediaQuery';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const PAGE_SIZE = 20;
const QUICK_AMOUNTS = [10000, 20000, 50000, 100000];
type Filter = 'ALL' | PettyCashStatus;
const FILTERS: Filter[] = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'];
const STATUS_TONE: Record<PettyCashStatus, 'warning' | 'success' | 'danger'> = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger' };

/** Petty cash routes return meta.errors as a plain string on failure. */
const pettyCashError = (err: unknown) => {
    if (axios.isAxiosError(err)) {
        const errors = err.response?.data?.meta?.errors;
        if (typeof errors === 'string' && errors) return errors;
    }
    return getErrorMessage(err);
};

/** Timestamps are naive server-local strings; formatters' toLocalDate would shift them by +7h. */
const whenOf = (iso: string) => new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const openProof = (url: string) => window.open(url, '_blank', 'noopener');

/**
 * Kas kecil — balance hero, filterable ledger with a detail pane (lg) / sheet (phone),
 * and a submit form (photo · name/desc · amount) as a second view of the same page.
 */
const PettyCashPage: React.FC = () => {
    const { t } = useTranslation();
    const { selectedShop } = useAppSelector((s) => s.auth);
    const isLandscape = useIsLandscape();
    const [summary, setSummary] = useState<PettyCashSummary | null>(null);
    const [entries, setEntries] = useState<PettyCashEntry[]>([]);
    const [page, setPage] = useState(0);
    const [last, setLast] = useState(true);
    const [filter, setFilter] = useState<Filter>('ALL');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [view, setView] = useState<'ledger' | 'submit'>('ledger');
    // submit form
    const [photo, setPhoto] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [photoError, setPhotoError] = useState<string | null>(null);
    const [nameError, setNameError] = useState<string | null>(null);
    const [amountError, setAmountError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [uploadFailed, setUploadFailed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    const shopId = selectedShop?.shopId;

    const loadSummary = useCallback(async () => {
        if (!shopId) return;
        try {
            const res = await api.get<ApiResponse<PettyCashSummary>>(API_ENDPOINTS.PETTY_CASH_SUMMARY(shopId));
            setSummary(res.data.data);
        } catch (e) {
            setError(pettyCashError(e));
        }
    }, [shopId]);

    const load = useCallback(async (p: number, replace: boolean) => {
        if (!shopId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await api.get<ApiResponse<PagedResponse<PettyCashEntry>>>(API_ENDPOINTS.PETTY_CASH(shopId), {
                params: { status: filter === 'ALL' ? undefined : filter, page: p, size: PAGE_SIZE },
            });
            const content = res.data.data.content || [];
            setEntries((prev) => (replace ? content : [...prev, ...content]));
            setLast(res.data.data.page?.last ?? content.length < PAGE_SIZE);
            setPage(p);
        } catch (e) {
            setError(pettyCashError(e));
        } finally {
            setLoading(false);
        }
    }, [shopId, filter]);

    const refresh = useCallback(() => { loadSummary(); load(0, true); }, [loadSummary, load]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        if (!photo) { setPreview(null); return; }
        const url = URL.createObjectURL(photo);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [photo]);

    if (!selectedShop || !shopId) return null;

    const selected = entries.find((e) => e.id === selectedId) ?? (isLandscape ? entries[0] : undefined) ?? null;
    const amountNum = Number(amount || 0);
    const photoTooLarge = !!photo && photo.size > MAX_IMAGE_BYTES;
    const canSubmit = !!photo && !photoTooLarge && name.trim().length > 0 && name.trim().length <= 150 && Number.isInteger(amountNum) && amountNum > 0;

    const pickPhoto = (f: File | null) => {
        setPhoto(f);
        setPhotoError(f && f.size > MAX_IMAGE_BYTES ? t('pettyCash.photoTooLarge') : null);
        setUploadFailed(false);
    };
    const resetForm = () => { setPhoto(null); setName(''); setAmount(''); setDescription(''); setPhotoError(null); setNameError(null); setAmountError(null); setFormError(null); setUploadFailed(false); };
    const openSubmit = () => { resetForm(); setView('submit'); };
    const statusLabel = (s: PettyCashStatus) => t(`pettyCash.status.${s}` as 'pettyCash.status.PENDING');

    const submit = async () => {
        setFormError(null); setNameError(null); setAmountError(null);
        if (!photo) { setPhotoError(t('pettyCash.photoRequired')); return; }
        if (photoTooLarge) { setPhotoError(t('pettyCash.photoTooLarge')); return; }
        if (!name.trim()) { setNameError(t('pettyCash.nameRequired')); return; }
        if (!Number.isInteger(amountNum) || amountNum <= 0) { setAmountError(t('pettyCash.amountInvalid')); return; }
        setSubmitting(true);
        try {
            const form = new FormData();
            form.append('proof', photo, 'proof.jpg');
            form.append('name', name.trim());
            form.append('amount', String(amountNum));
            if (description.trim()) form.append('description', description.trim());
            const res = await api.post<ApiResponse<PettyCashEntry>>(API_ENDPOINTS.PETTY_CASH(shopId), form, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(t('pettyCash.submitted', { name: res.data.data.name, amount: formatCurrency(res.data.data.amount) }));
            resetForm();
            setView('ledger');
            if (filter === 'ALL') refresh(); else setFilter('ALL'); // filter change refetches via the effect
        } catch (e) {
            const msg = pettyCashError(e);
            switch (classifyPettyCashError(msg)) {
                case 'photoRequired': setPhotoError(t('pettyCash.photoRequired')); setPhoto(null); break;
                case 'photoTooLarge': setPhotoError(t('pettyCash.photoTooLarge')); setPhoto(null); break;
                case 'uploadFailed': setUploadFailed(true); break;
                case 'photo': setPhotoError(msg); setPhoto(null); break;
                case 'name': setNameError(msg); break;
                case 'amount': setAmountError(msg); break;
                case 'inactive': setFormError(t('pettyCash.inactiveUser')); break;
                default: setFormError(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const detail = (e: PettyCashEntry) => {
        const isIn = e.entryType === 'IN';
        const facts: [string, string][] = [
            [e.fromWithdrawal ? t('pettyCash.source') : t('pettyCash.recordedBy'), e.fromWithdrawal ? t('pettyCash.withdrawal') : e.submittedByName ?? '—'],
            [t('pettyCash.time'), whenOf(e.createdAt)],
            [t('pettyCash.reviewedBy'), e.reviewedByName ?? t('pettyCash.notReviewed')],
            [t('pettyCash.reviewedAt'), e.reviewedAt ? whenOf(e.reviewedAt) : '—'],
        ];
        return (
            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2.5">
                    <Overline className="flex-1">{isIn ? t('pettyCash.in') : t('pettyCash.out')} · #{e.id}</Overline>
                    <Badge tone={STATUS_TONE[e.status]} dot>{statusLabel(e.status)}</Badge>
                </div>
                <div className="flex flex-col gap-1">
                    <span className={clsx('text-[38px] font-black tnum leading-[1.1] tracking-[-0.02em]', isIn ? 'text-success-700' : 'text-ink-brand')}>{isIn ? '+' : '−'} {formatCurrency(e.amount)}</span>
                    <span className="text-[19px] font-black">{e.name}</span>
                    {e.description && <span className="text-sm leading-relaxed text-ink-secondary">{e.description}</span>}
                </div>
                {e.status === 'REJECTED' && e.reviewerNote && <Alert variant="error" title={t('pettyCash.rejectedNote')} message={e.reviewerNote} />}
                {e.status === 'PENDING' && <Alert variant="warning" message={t('pettyCash.pendingHint')} />}
                {e.fromWithdrawal && <Alert variant="info" message={t('pettyCash.fromWithdrawalHint')} />}
                {e.proofImageUrl ? (
                    <button type="button" onClick={() => openProof(e.proofImageUrl!)} className="relative h-[168px] shrink-0 overflow-hidden rounded-lg border border-line-subtle bg-surface-sunken">
                        <img src={e.proofImageUrl} alt="" className="h-full w-full object-cover" />
                        <span className="absolute bottom-2.5 right-2.5 flex h-[30px] items-center gap-1.5 rounded-pill bg-[#2d1227]/70 px-3 text-xs font-extrabold text-white backdrop-blur"><Maximize2 className="h-[13px] w-[13px]" />{t('pettyCash.viewProof')}</span>
                    </button>
                ) : !e.fromWithdrawal && (
                    <span className="flex items-center gap-2 text-[13px] text-ink-muted"><UserCog className="h-4 w-4" />{t('pettyCash.ownerEntry')}</span>
                )}
                <div className="flex flex-col border-t border-line-subtle">
                    {facts.map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-3 border-b border-line-subtle py-2.5 text-sm"><span className="text-ink-muted">{k}</span><span className="text-right font-extrabold">{v}</span></div>
                    ))}
                </div>
            </div>
        );
    };

    if (view === 'submit') {
        const photoSlot = photoTooLarge ? (
            <div className="flex min-h-[220px] flex-1 flex-col items-center justify-center gap-3 rounded-xl border-2 border-status-danger bg-status-danger-soft p-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-card text-status-danger"><ImageOff className="h-7 w-7" /></span>
                <span className="text-[17px] font-black text-danger-700">{t('pettyCash.photoTooLarge')} · {formatFileSize(photo!.size)}</span>
                <span className="text-[13px] leading-relaxed">{t('pettyCash.photoTooLargeHint')}</span>
            </div>
        ) : preview ? (
            <div className="relative min-h-[220px] flex-1 overflow-hidden rounded-xl border border-line-subtle bg-surface-sunken">
                <img src={preview} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <span className="absolute left-3 top-3 flex h-7 items-center gap-1.5 rounded-pill bg-surface-card px-2.5 text-xs font-extrabold text-success-700 shadow-xs"><Check className="h-[13px] w-[13px]" />{formatFileSize(photo!.size)}</span>
            </div>
        ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className={clsx('flex min-h-[220px] flex-1 flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed bg-surface-card p-6 text-center hover:border-accent-500 hover:bg-accentsoft', photoError ? 'border-status-danger' : 'border-line')}>
                <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-accent-500 text-ink-onAccent shadow-accent"><Camera className="h-[30px] w-[30px]" /></span>
                <span className="text-[17px] font-black">{t('pettyCash.takePhoto')}</span>
                <span className="text-[13px] leading-relaxed text-ink-muted">{t('pettyCash.photoHint')}</span>
            </button>
        );
        const cta = <Button variant="primary" size="lg" fullWidth disabled={!canSubmit} loading={submitting} onClick={submit}>{uploadFailed ? t('pettyCash.resend') : t('pettyCash.send')}</Button>;
        return (
            <PageShell
                close
                title={t('pettyCash.submit')}
                subtitle={`${selectedShop.shopName}${summary ? ` · ${t('common.balance')} ${formatCurrency(summary.balance)}` : ''}`}
                onBack={() => setView('ledger')}
                footer={<div className="lg:hidden">{cta}</div>}
            >
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { pickPhoto(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                <div className="flex flex-col gap-4 pb-5 lg:grid lg:h-full lg:grid-cols-[340px_minmax(0,1fr)_360px] lg:gap-5 lg:pb-6">
                    <div className="flex flex-col gap-2.5 lg:min-h-0">
                        <span className="text-[13px] font-extrabold text-ink-secondary">{t('pettyCash.photo')} <span className="text-status-danger">*</span></span>
                        {photoSlot}
                        {photoError && !photoTooLarge && <p className="text-xs font-semibold text-status-danger">{photoError}</p>}
                        {photo && (
                            <div className="flex gap-2.5">
                                <Button variant={photoTooLarge ? 'primary' : 'secondary'} fullWidth icon={<Camera />} onClick={() => fileRef.current?.click()}>{t('pettyCash.retake')}</Button>
                                {!photoTooLarge && <Button variant="ghost" icon={<Trash />} onClick={() => pickPhoto(null)}>{t('pettyCash.remove')}</Button>}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-4 lg:min-w-0">
                        {uploadFailed && <Alert variant="error" title={t('pettyCash.uploadFailed')} message={<span className="flex items-center gap-2"><CloudOff className="h-4 w-4 shrink-0" />{t('pettyCash.uploadFailedHint')}</span>} />}
                        {formError && <Alert variant="error" message={formError} />}
                        <Input label={t('pettyCash.name')} required maxLength={150} value={name} onChange={(e) => { setName(e.target.value); setNameError(null); }} placeholder={t('pettyCash.namePlaceholder')} error={nameError ?? undefined} helperText={`${name.length}/150`} fieldSize="lg" className="!font-extrabold" />
                        <Input label={`${t('pettyCash.description')} ${t('pettyCash.optional')}`} multiline rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('pettyCash.descriptionPlaceholder')} />
                        <div className="hidden flex-1 lg:block" />
                        <Alert variant="brand" message={t('pettyCash.submitHint')} />
                    </div>
                    <div className="flex flex-col gap-3 rounded-xl border border-line-subtle bg-surface-card p-4 shadow-sm lg:min-h-0">
                        <Input
                            label={t('pettyCash.amount')}
                            required
                            numeric
                            inputMode="numeric"
                            placeholder="0"
                            value={amount}
                            onChange={(e) => { setAmount(e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 12)); setAmountError(null); }}
                            error={amountError ?? undefined}
                            leftIcon={<span className="text-xl font-extrabold text-ink-muted">Rp</span>}
                            wrapperClassName="!h-16 !border-2 !border-accent-500 shadow-focus lg:!h-[68px]"
                            className="!text-[28px] lg:!text-[32px]"
                        />
                        <div className="flex flex-wrap gap-2">
                            {QUICK_AMOUNTS.map((v) => <Chip key={v} selected={amountNum === v} onClick={() => setAmount(String(v))}>{formatCurrency(v)}</Chip>)}
                        </div>
                        <NumericKeypad className="hidden min-h-0 flex-1 lg:grid" value={amount} onChange={setAmount} />
                        <div className="hidden lg:block">{cta}</div>
                    </div>
                </div>
            </PageShell>
        );
    }

    const submitButton = (props: { size?: 'md' | 'lg'; fullWidth?: boolean; className?: string }) => (
        <Button variant="primary" icon={<Plus />} onClick={openSubmit} {...props}>{t('pettyCash.submit')}</Button>
    );

    return (
        <div className="flex h-full min-h-0 flex-1">
            <PageShell
                title={t('pettyCash.title')}
                subtitle={selectedShop.shopName}
                backTo="/settings"
                className="lg:border-r lg:border-line-subtle"
                actions={<>
                    <IconButton variant="card" label={t('common.refresh')} onClick={refresh} disabled={loading}><RefreshCw className={clsx(loading && 'animate-spin')} /></IconButton>
                    {submitButton({ className: 'hidden lg:inline-flex' })}
                </>}
                footer={<div className="lg:hidden">{submitButton({ size: 'lg', fullWidth: true })}</div>}
            >
                <div className="flex flex-col gap-3 pb-5">
                    {error && <Alert variant="error" message={error} />}
                    <div className="flex flex-col gap-4 rounded-xl bg-surface-brand p-5 text-white shadow-brand lg:flex-row lg:items-stretch lg:gap-6 lg:px-6">
                        <div className="flex flex-1 flex-col gap-1">
                            <Overline tone="onBrand">{t('pettyCash.balance')}</Overline>
                            <span className={clsx('text-metric tnum', summary && summary.balance < 0 && 'text-danger-200')}>{summary ? formatCurrency(summary.balance) : '—'}</span>
                            <span className="text-[13px] text-primary-200">{t('pettyCash.balanceHint')}</span>
                        </div>
                        <div className="h-px bg-white/15 lg:h-auto lg:w-px" />
                        <div className="flex flex-col justify-center gap-1 lg:w-[250px]">
                            <span className="flex items-center gap-1.5 text-[13px] font-extrabold text-primary-100"><Hourglass className="h-[15px] w-[15px]" />{t('pettyCash.pendingOut')}</span>
                            <span className="text-[26px] font-black tnum">{summary ? formatCurrency(summary.pendingOutTotal) : '—'}</span>
                            <span className="text-[13px] text-primary-200">{t('pettyCash.pendingCount', { count: summary?.pendingCount ?? 0 })}</span>
                        </div>
                    </div>
                    <div className="flex gap-2 overflow-x-auto">
                        {FILTERS.map((f) => (
                            <Chip key={f} selected={filter === f} onClick={() => setFilter(f)}>
                                {f === 'ALL' ? t('pettyCash.filterAll') : f === 'PENDING' && summary ? `${statusLabel(f)} · ${summary.pendingCount}` : statusLabel(f)}
                            </Chip>
                        ))}
                    </div>
                    {loading && entries.length === 0 && [1, 2, 3].map((i) => <LoadingSkeleton key={i} height={72} />)}
                    {!loading && entries.length === 0 && (
                        <div className="flex flex-col items-center gap-3 py-12 text-center">
                            <span className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-brandsoft text-ink-brand"><Wallet className="h-[38px] w-[38px]" /></span>
                            <span className="text-[22px] font-black">{t('pettyCash.empty')}</span>
                            <span className="max-w-[380px] text-[15px] leading-relaxed text-ink-secondary">{t('pettyCash.emptyHint')}</span>
                        </div>
                    )}
                    {entries.map((e) => {
                        const isIn = e.entryType === 'IN';
                        const active = isLandscape && selected?.id === e.id;
                        return (
                            <button
                                key={e.id}
                                type="button"
                                onClick={() => { setSelectedId(e.id); if (!isLandscape) setDetailOpen(true); }}
                                className={clsx('flex items-center gap-3.5 rounded-lg px-4 py-3 text-left', active ? 'border-2 border-accent-500 bg-accentsoft' : 'border border-line-subtle bg-surface-card')}
                            >
                                <span className={clsx('flex h-11 w-11 shrink-0 items-center justify-center rounded-full', isIn ? 'bg-status-success-soft text-success-700' : 'bg-surface-sunken text-ink-brand')}>
                                    {isIn ? <ArrowDownLeft className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
                                </span>
                                <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                                    <span className="flex items-center gap-2"><span className="truncate text-base font-extrabold">{e.name}</span>{e.fromWithdrawal && <Badge tone="info">{t('pettyCash.fromWithdrawal')}</Badge>}</span>
                                    <span className="flex items-center gap-1.5 text-[13px] text-ink-muted">{e.proofImageUrl && <Image className="h-3.5 w-3.5" />}{e.fromWithdrawal ? t('pettyCash.auto') : e.submittedByName ?? '—'} · {whenOf(e.createdAt)}</span>
                                </span>
                                <span className="flex flex-col items-end gap-1.5">
                                    <span className={clsx('text-base font-black tnum', isIn && 'text-success-700')}>{isIn ? '+' : '−'} {formatCurrency(e.amount)}</span>
                                    <Badge tone={STATUS_TONE[e.status]} dot>{statusLabel(e.status)}</Badge>
                                </span>
                            </button>
                        );
                    })}
                    {!last && <Button variant="outline" onClick={() => load(page + 1, false)} loading={loading} className="mt-2 self-center">{t('common.loadMore')}</Button>}
                </div>
            </PageShell>

            <aside className="hidden w-[420px] shrink-0 overflow-y-auto bg-surface-card p-6 lg:block">
                {selected ? detail(selected) : (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-ink-muted">
                        <Receipt className="h-9 w-9 text-primary-200" />
                        <p className="text-sm font-bold">{t('pettyCash.selectHint')}</p>
                    </div>
                )}
            </aside>

            <Sheet isOpen={detailOpen && !isLandscape && !!selected} onClose={() => setDetailOpen(false)} showCloseButton={false}>
                {selected && detail(selected)}
            </Sheet>
        </div>
    );
};

export default PettyCashPage;
